import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import useAuthStore from '../../store/authStore';
import api from '../../utils/api';
import { FluxoEmpresa, faseAtualEmpresa } from '../../components/FluxoJornada';

export default function DashboardEmpresa() {
  const { user }  = useAuthStore();
  const [dash,    setDash]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/empresas/dashboard')
      .then(r => { setDash(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // ── Dados do acesso ──────────────────────────────────────────
  const status        = user?.status_pagamento;
  const expiracao     = dash?.acesso?.data_expiracao || user?.data_expiracao;
  const diasRestantes = dash?.acesso?.dias_restantes ??
    (expiracao ? Math.max(0, Math.ceil((new Date(expiracao) - new Date()) / 86400000)) : null);
  const isTrial = status === 'ativo' && diasRestantes !== null && diasRestantes <= 30;

  // ── Dados das vagas ──────────────────────────────────────────
  const vagas         = dash?.vagas || {};
  const vagasRecentes = dash?.vagas_recentes || [];
  const selecao       = dash?.selecao || { total: 0, em_selecao: 0, entrevista: 0, aprovados: 0 };

  // ── Banner de acesso ─────────────────────────────────────────
  const AlerteAcesso = () => {
    if (status === 'ativo') {
      // Trial com poucos dias — urgente
      if (isTrial && diasRestantes <= 3) return (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-5 flex items-center justify-between gap-4">
          <div>
            <p className="font-bold text-red-800 text-sm">🚨 Período gratuito encerrando!</p>
            <p className="text-red-700 text-xs mt-0.5">
              Restam <strong>{diasRestantes} dia{diasRestantes !== 1 ? 's' : ''}</strong> do trial.
              Assine para não perder o acesso.
            </p>
          </div>
          <Link to="/empresa/pagamento" className="btn-gold text-xs px-4 py-2 flex-shrink-0">Assinar →</Link>
        </div>
      );

      // Trial ativo
      if (isTrial) return (
        <div className="bg-gradient-to-r from-acre-gold/10 to-yellow-50 border border-acre-gold/40 rounded-xl p-4 mb-5 flex items-center justify-between gap-4">
          <div>
            <p className="font-bold text-amber-800 text-sm">
              🎁 Período gratuito — {diasRestantes} dia{diasRestantes !== 1 ? 's' : ''} restante{diasRestantes !== 1 ? 's' : ''}
            </p>
            <p className="text-amber-700 text-xs mt-0.5">
              Acesso completo à plataforma.
              {expiracao && ` Expira em ${new Date(expiracao).toLocaleDateString('pt-BR')}.`}
            </p>
          </div>
          <Link to="/empresa/pagamento" className="btn-gold text-xs px-4 py-2 flex-shrink-0">Ver planos →</Link>
        </div>
      );

      // Assinatura paga
      return (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-5 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-green-800 text-sm">✅ Assinatura Ativa</p>
            <p className="text-green-700 text-xs mt-0.5">
              {diasRestantes !== null ? `${diasRestantes} dias restantes` : 'Acesso liberado'}
              {expiracao && ` · expira ${new Date(expiracao).toLocaleDateString('pt-BR')}`}
            </p>
          </div>
          {diasRestantes !== null && diasRestantes <= 7 && (
            <Link to="/empresa/pagamento" className="btn-gold text-xs px-4 py-2 flex-shrink-0">Renovar →</Link>
          )}
        </div>
      );
    }

    if (status === 'aguardando_aprovacao') return (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
        <p className="font-semibold text-blue-800 text-sm">⏳ Pagamento em análise</p>
        <p className="text-blue-700 text-xs mt-0.5">Aguarde a confirmação (em até 24h úteis).</p>
      </div>
    );

    if (status === 'expirado') return (
      <div className="bg-orange-50 border border-orange-300 rounded-xl p-4 mb-5 flex items-center justify-between gap-4">
        <div>
          <p className="font-bold text-orange-800 text-sm">⏰ Período gratuito encerrado</p>
          <p className="text-orange-700 text-xs mt-0.5">Seus 15 dias expiraram. Assine para retomar.</p>
        </div>
        <Link to="/empresa/pagamento" className="btn-gold text-xs px-4 py-2 flex-shrink-0">Assinar →</Link>
      </div>
    );

    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-5 flex items-center justify-between gap-4">
        <div>
          <p className="font-semibold text-yellow-800 text-sm">🔒 Acesso Bloqueado</p>
          <p className="text-yellow-700 text-xs mt-0.5">Assine um plano para liberar o acesso.</p>
        </div>
        <Link to="/empresa/pagamento" className="btn-gold text-xs px-4 py-2 flex-shrink-0">Assinar →</Link>
      </div>
    );
  };

  return (
    <Layout title="Painel da Empresa" subtitle="Gerencie suas vagas e candidatos">

      <AlerteAcesso/>

      {/* ── Fluxo de contratação ── */}
      <FluxoEmpresa faseAtual={faseAtualEmpresa({
        temVagas:     (vagas.total || 0) > 0,
        temMatches:   vagasRecentes.some(v => (v.total_candidatos || 0) > 0),
        temSelecao:   (selecao.em_selecao || 0) > 0 || (selecao.entrevista || 0) > 0,
        temAprovados: (selecao.aprovados || 0) > 0,
      })}/>

      {/* ── Cards de estatísticas ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          {
            num: loading ? '...' : (vagas.abertas ?? 0),
            label: 'Vagas abertas',
            cor: 'text-acre-green',
          },
          {
            num: loading ? '...' : (vagas.total ?? 0),
            label: 'Total de vagas',
            cor: 'text-acre-dark',
          },
          {
            num: loading ? '...' : (vagas.total_visualizacoes ?? 0),
            label: 'Visualizações',
            cor: 'text-blue-600',
          },
          {
            num: loading ? '...' :
              status === 'ativo' ? (diasRestantes !== null ? diasRestantes : '∞') : '🔒',
            label: status === 'ativo' ? 'Dias restantes' : 'Sem acesso',
            cor: diasRestantes !== null && diasRestantes <= 7 ? 'text-red-500' : 'text-acre-gold',
          },
        ].map((s, i) => (
          <div key={i} className="card p-4 text-center">
            <div className={`font-sora text-2xl font-bold ${s.cor}`}>{s.num}</div>
            <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Cards de seleção ── */}
      {(selecao.total || 0) > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { num: selecao.em_selecao || 0, label: 'Em Seleção',  cor: 'text-blue-600',   bg: 'bg-blue-50'  },
            { num: selecao.entrevista || 0, label: 'Entrevista',  cor: 'text-yellow-600', bg: 'bg-yellow-50'},
            { num: selecao.aprovados || 0,  label: 'Aprovados',   cor: 'text-green-600',  bg: 'bg-green-50' },
          ].map((s, i) => (
            <div key={i} className={`card p-3 text-center ${s.bg}`}>
              <div className={`font-sora text-xl font-bold ${s.cor}`}>{s.num}</div>
              <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Lista de vagas ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="section-title">Minhas Vagas</h2>
          <div className="flex gap-2">
            <Link to="/empresa/vagas" className="btn-outline text-xs px-3 py-2">Gerenciar</Link>
            <Link to="/empresa/vagas/nova" className="btn-primary text-xs px-4 py-2">+ Nova Vaga</Link>
          </div>
        </div>

        {loading ? (
          <div className="card p-8 text-center text-gray-400">
            <div className="w-6 h-6 border-4 border-acre-green border-t-transparent rounded-full animate-spin mx-auto mb-2"/>
            Carregando vagas...
          </div>
        ) : vagasRecentes.length === 0 ? (
          <div className="card p-8 text-center">
            <p className="text-4xl mb-2">📋</p>
            <p className="text-gray-500 text-sm mb-3">Nenhuma vaga cadastrada ainda.</p>
            <Link to="/empresa/vagas/nova" className="btn-primary inline-block text-sm px-5 py-2.5">
              + Criar primeira vaga
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {vagasRecentes.map(v => (
              <div key={v.id} className="card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-sora font-semibold text-acre-dark text-sm truncate">{v.titulo}</span>
                      <span className={`text-xs font-semibold border px-2 py-0.5 rounded-full ${
                        v.status === 'aberta'
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-gray-100 text-gray-500 border-gray-200'
                      }`}>
                        {v.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {v.cidade_vaga}
                      {v.total_candidatos > 0
                        ? ` · 💚 ${v.total_candidatos} candidato${v.total_candidatos !== 1 ? 's' : ''} interessado${v.total_candidatos !== 1 ? 's' : ''}`
                        : ' · Aguardando candidatos'}
                      {v.media_score > 0 && ` · média ${v.media_score}% match`}
                    </p>
                  </div>
                  {status === 'ativo' ? (
                    <Link to={`/empresa/match/${v.id}`}
                      className="btn-primary text-xs px-3 py-2 flex-shrink-0">
                      🎯 Ver Matches
                    </Link>
                  ) : (
                    <Link to="/empresa/pagamento"
                      className="text-xs text-gray-400 bg-gray-50 border border-gray-200 px-3 py-2 rounded-lg flex-shrink-0">
                      🔒 Assinar
                    </Link>
                  )}
                </div>
              </div>
            ))}
            {(vagas.total || 0) > vagasRecentes.length && (
              <Link to="/empresa/vagas"
                className="block text-center text-xs text-acre-green hover:underline py-2">
                Ver todas as {vagas.total} vagas →
              </Link>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
